// src/components/AssetList/AssetList.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";

const AssetList = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestId, setRequestId] = useState("");
  const [timestamp, setTimestamp] = useState("");

  const fetchAllAssets = async () => {
    const startTime = Date.now();
    const newRequestId = uuidv4();
    const newTimestamp = new Date().toISOString();

    setRequestId(newRequestId);
    setTimestamp(newTimestamp);
    setLoading(true);
    setError(null);
    setAssets([]);

    try {
      const token = "3585535a-137c-499e-82ad-af9feff7d8e7";

      if (!token) {
        const errorMessage =
          "Client misconfiguration: REACT_APP_TOKEN_ID is missing in environment";
        console.warn(`[${newRequestId}] Missing token in environment`);
        throw {
          custom: true,
          requestId: newRequestId,
          message: errorMessage,
          details: {
            required_env_variable: "REACT_APP_TOKEN_ID",
            example: "REACT_APP_TOKEN_ID=3acef045-d302-4032-b40a-d9ee6c1519cd",
            documentation:
              "https://api.yourservice.com/docs/assets#authentication",
          },
          timestamp: newTimestamp,
        };
      }

      const endpoints = [
        `${process.env.REACT_APP_API_BASE_URL}/OpenApi/Admin`,
        "http://icloud.assetscontrols.com:8092/OpenApi/Admin",
        "https://icloud.assetscontrols.com:3443/OpenApi/Admin",
      ].filter(Boolean);

      const payload = {
        FAction: "QueryAdminVehicleList",
        FTokenID: token,
        FPageIndex: 1,
        FPageSize: 1000,
      };

      console.debug(`[${newRequestId}] Attempting endpoints`, { endpoints });

      let lastError = null;

      for (const [index, endpoint] of endpoints.entries()) {
        try {
          const response = await axios.post(endpoint, payload, {
            headers: {
              "Content-Type": "application/json",
              "X-Request-ID": newRequestId,
              "X-Client-Version": "1.0.0",
            },
            timeout: 8000,
          });

          console.debug(`[${newRequestId}] API Response`, {
            endpoint,
            status: response.status,
            data: response.data,
          });

          if (response.data?.Result === 200) {
            const assetsData = Array.isArray(response.data.FObject)
              ? response.data.FObject
              : [];

            if (assetsData.length === 0) {
              console.warn(`[${newRequestId}] Empty asset response`, {
                endpoint,
                accountInfo: response.headers["X-Account-Info"],
              });

              setError({
                custom: true,
                requestId: newRequestId,
                message: "No assets found for this account",
                suggestions: [
                  "Verify asset permissions in account settings",
                  "Check if assets are assigned to your organization",
                  "Try refreshing your authentication token",
                  "Contact support with code: NO_ASSETS_FOUND",
                ],
                diagnostics: {
                  apiEndpointUsed: endpoint,
                  responseTime: `${Date.now() - startTime}ms`,
                  accountType: response.headers["X-Account-Type"] || "unknown",
                },
                timestamp: newTimestamp,
              });
              return;
            }

            setAssets(
              assetsData.map((asset) => ({
                id: asset.FGUID,
                name: asset.FVehicleName,
                device: asset.FAssetID
                  ? {
                      id: asset.FAssetID,
                      type: asset.FAssetTypeID,
                    }
                  : null,
                metadata: {
                  description: asset.FDescription,
                  vin: asset.FVIN,
                  createdAt: asset.FCreateTime,
                },
                organization: {
                  id: asset.FAgentGUID,
                  name: asset.FAgentName,
                },
              }))
            );
            return;
          }

          const errorMap = {
            102: { status: 400, message: "Invalid request parameters" },
            104: {
              status: 401,
              message: "Authentication token expired or invalid",
            },
            105: { status: 500, message: "Server configuration error" },
          };

          const errorInfo = errorMap[response.data?.Result] || {
            status: 400,
            message: response.data?.Message || "Unknown API error",
          };

          lastError = {
            custom: false,
            requestId: newRequestId,
            message: errorInfo.message,
            details: response.data,
            timestamp: newTimestamp,
          };
        } catch (error) {
          console.error(`[${newRequestId}] Endpoint failed`, {
            endpoint,
            error: error.message,
            stack: error.stack,
          });

          lastError = {
            custom: false,
            requestId: newRequestId,
            message: error.message,
            details: {
              endpoint,
              errorCode: error.code,
              responseStatus: error.response?.status,
            },
            timestamp: newTimestamp,
          };

          if (index < endpoints.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            continue;
          }
        }
      }

      throw (
        lastError || {
          custom: false,
          requestId: newRequestId,
          message: "All API endpoints failed",
          details: {
            attemptedEndpoints: endpoints,
          },
          timestamp: newTimestamp,
        }
      );
    } catch (error) {
      console.error(`[${requestId}] Error fetching assets`, error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllAssets();
  }, []);

  return (
    <div className="asset-list-container py-4 max-w-6xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Asset Inventory</h2>

      <div className="controls mb-6 flex justify-between items-center">
        <button
          onClick={fetchAllAssets}
          disabled={loading}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded disabled:bg-gray-400"
        >
          {loading ? "Refreshing..." : "Refresh Assets"}
        </button>

        {requestId && (
          <div className="request-info text-sm text-gray-500">
            {/* Request ID: {requestId} */}
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-indicator p-4 bg-blue-50 rounded mb-4">
          <p>Fetching asset data...</p>
        </div>
      )}

      {error && (
        <div className="error-message p-4 bg-red-50 border-l-4 border-red-500 rounded mb-4">
          <h3 className="font-bold text-red-700">{error.message}</h3>

          {error.details && (
            <div className="error-details mt-3">
              {error.suggestions && (
                <div className="suggestions mb-3">
                  <h4 className="font-semibold text-sm">Suggestions:</h4>
                  <ul className="list-disc list-inside text-sm">
                    {error.suggestions.map((suggestion, i) => (
                      <li key={i}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="technical-details p-2 bg-gray-100 rounded text-xs">
                <pre>{JSON.stringify(error.details, null, 2)}</pre>
              </div>
            </div>
          )}

          <div className="mt-3 text-xs text-gray-500">
            Timestamp: {error.timestamp || timestamp}
          </div>
        </div>
      )}

      {assets.length > 0 ? (
        <div className="asset-grid">
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white shadow rounded-lg overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 text-left">Name</th>
                  <th className="py-3 px-4 text-left">Device ID</th>
                  <th className="py-3 px-4 text-left">Organization</th>
                  <th className="py-3 px-4 text-left">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {assets.map((asset) => (
                  <tr key={asset.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium">{asset.name}</div>
                      {asset.metadata.description && (
                        <div className="text-sm text-gray-500">
                          {asset.metadata.description}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {asset.device ? (
                        <>
                          <div>{asset.device.id}</div>
                          <div className="text-sm text-gray-500">
                            Type: {asset.device.type}
                          </div>
                        </>
                      ) : (
                        <span className="text-gray-400">No device</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div>{asset.organization.name}</div>
                      <div className="text-sm text-gray-500">
                        {asset.organization.id}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {new Date(asset.metadata.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-500">
            Showing {assets.length} assets | Last updated: {timestamp}
          </div>
        </div>
      ) : (
        !loading &&
        !error && (
          <div className="empty-state p-8 text-center bg-gray-50 rounded-lg">
            <h3 className="text-lg font-medium text-gray-700">
              No assets found
            </h3>
            <p className="mt-2 text-gray-500">
              The system returned an empty asset list
            </p>
          </div>
        )
      )}
    </div>
  );
};

export default AssetList;
